import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, AlertCircle, Info, CheckCircle2, Download, X } from 'lucide-react';
import { runPreflight, type PreflightIssue, type PreflightSeverity } from '@fotoboek/domain';
import { useProjectStore } from '../../state/projectStore';
import { startPdfExport } from '../../export/exportClient';
import { downloadBlob, slugify } from '../../lib/download';
import { useUiStore } from '../../state/uiStore';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { t } from '../../i18n';
import './export-page.css';

const SEVERITY_ORDER: PreflightSeverity[] = ['blocking', 'error', 'warning', 'info'];
const SEVERITY_ICON = {
  blocking: AlertCircle,
  error: AlertTriangle,
  warning: AlertTriangle,
  info: Info,
};

export default function ExportPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const project = useProjectStore((s) => s.project);
  const status = useProjectStore((s) => s.status);
  const loadProject = useProjectStore((s) => s.loadProject);
  const reset = useProjectStore((s) => s.reset);
  const mutate = useProjectStore((s) => s.mutate);
  const pushToast = useUiStore((s) => s.pushToast);

  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [exportHandle, setExportHandle] = useState<ReturnType<typeof startPdfExport> | null>(null);

  useEffect(() => {
    if (projectId) void loadProject(projectId);
    return () => reset();
  }, [projectId, loadProject, reset]);

  const issues = useMemo<PreflightIssue[]>(() => (project ? runPreflight(project) : []), [project]);
  const blockingCount = issues.filter((i) => i.severity === 'blocking').length;

  const goToIssue = (issue: PreflightIssue) => {
    if (!project) return;
    const params = new URLSearchParams();
    if (issue.pageId) params.set('page', issue.pageId);
    if (issue.coverPanel) params.set('panel', issue.coverPanel);
    if (issue.elementId) params.set('element', issue.elementId);
    navigate(`/editor/${project.id}?${params.toString()}`);
  };

  const handleExport = () => {
    if (!project) return;
    setExporting(true);
    setProgress({ current: 0, total: project.pages.length + 3 });
    const handle = startPdfExport(
      project.id,
      { dpi: undefined, includeBleed: true },
      (current, total) => setProgress({ current, total }),
    );
    setExportHandle(handle);
    handle.promise
      .then((blob) => {
        downloadBlob(blob, `${slugify(project.title)}.pdf`);
        pushToast({ kind: 'success', message: t('export.success') });
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') {
          pushToast({ kind: 'info', message: t('export.cancelled') });
        } else {
          pushToast({ kind: 'error', message: t('export.failure') });
        }
      })
      .finally(() => {
        setExporting(false);
        setExportHandle(null);
      });
  };

  if (status !== 'ready' || !project) {
    return (
      <div className="export-loading">
        <Spinner size={28} />
      </div>
    );
  }

  const grouped = SEVERITY_ORDER.map((sev) => ({
    severity: sev,
    items: issues.filter((i) => i.severity === sev),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="export-page">
      <div className="export-header">
        <h1>{t('export.title')}</h1>
        <p>{t('export.subtitle')}</p>
      </div>

      <div className="export-content">
        <div className="export-issues">
          {issues.length === 0 ? (
            <div className="export-no-issues">
              <CheckCircle2 size={18} />
              {t('export.noIssues')}
            </div>
          ) : (
            <>
              <p className="export-issues-count">
                {t('export.issuesFound', { count: issues.length })}
              </p>
              {grouped.map((group) => (
                <div key={group.severity} className="export-issue-group">
                  <div className={`export-severity-label ${group.severity}`}>
                    {t(`export.severities.${group.severity}`)}
                  </div>
                  {group.items.map((issue) => {
                    const Icon = SEVERITY_ICON[issue.severity];
                    return (
                      <div key={issue.id} className="export-issue-row">
                        <Icon size={15} />
                        <span>{issue.message}</span>
                        {issue.pageId || issue.coverPanel ? (
                          <button className="export-issue-link" onClick={() => goToIssue(issue)}>
                            {t('export.goToIssue')}
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ))}
              <label className="export-allow-low-dpi">
                <input
                  type="checkbox"
                  checked={project.settings.allowLowDpiLocalExport}
                  onChange={(e) =>
                    mutate('Instellingen bijwerken', (draft) => {
                      draft.settings.allowLowDpiLocalExport = e.target.checked;
                    })
                  }
                />
                {t('export.allowLowDpi')}
              </label>
            </>
          )}
        </div>

        <div className="export-action-panel">
          {exporting ? (
            <div className="export-progress">
              <Spinner size={24} />
              <p>
                {t('export.progressLabel', { current: progress.current, total: progress.total })}
              </p>
              <Button variant="secondary" onClick={() => exportHandle?.cancel()}>
                <X size={14} />
                {t('export.cancel')}
              </Button>
            </div>
          ) : (
            <Button variant="primary" size="lg" onClick={handleExport} disabled={blockingCount > 0}>
              <Download size={18} />
              {t('export.exportPdf')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
